import type { NodePath, ViewQueryRecordNode } from '../../schemas/query/index.ts'
import { getInnerRowSpan } from '../util/assignInnerRowGeometry.ts'
import { getRecordNodesByPath, type ViewQueryRecordNodeWithParent } from '../util/recordNodeUtility.ts'

export interface SheetCell {
  value: unknown
  label?: unknown // 現状はCSV作成のときのみで利用。各所でlabelを求めてしまっているので統一したい
  height: number
  innerRowIndexStart: number
  innerRowIndexEnd: number
  recordNode: undefined | ViewQueryRecordNodeWithParent
  parentRecordNode: undefined | ViewQueryRecordNodeWithParent // rootのときにundefined
}

type SheetCellNode = Omit<SheetCell, 'value'>

export interface SheetCellGroup {
  uiColumn: UiColumn | null
  innerCells: SheetCell[]
  height: number
}

// ツリー構造であるRecordNodeを、シートの1行の形に変更したもの
// シート行はカラムごとに情報を持ち、カラムは内部行(=セル)から構成される。
export interface SheetRow {
  height: number
  columns: SheetCellGroup[]
}

export interface UiColumn {
  nodePath: NodePath
  fieldName?: string // fieldNameがないケースは、systemColumnのときのみを想定
  selectOptions?: Array<{ label: string; value: string | number }> // 現状はCSV作成のときのみで利用
}

export const convertRecordNodeToInnerCells = (record: ViewQueryRecordNode, uiColumn: UiColumn): SheetCell[] => {
  // eslint-disable-next-line sonarjs/todo-tag
  const sheetCellNodes = getRecordNodesWithInnerRowIndex(record, uiColumn.nodePath, false) // TODO
  const innerCells = sheetCellNodes.map((x) => {
    const value = uiColumn.fieldName === undefined ? null : x.recordNode?.attributes[uiColumn.fieldName]

    const getLabel = () => {
      if (uiColumn.fieldName === undefined) {
        return null
      }
      const options = uiColumn.selectOptions ?? []
      if (options.length === 0) {
        return x.recordNode?.attributes[`${uiColumn.fieldName}_label`]
      }
      if (Array.isArray(value)) {
        return value.map((v) => options.find((x) => x.value === v)?.label)
      }
      return uiColumn.selectOptions?.find((x) => x.value === value)?.label
    }

    return {
      ...x,
      value,
      label: getLabel(),
    }
  })
  return innerCells
}

export function convertRecordNodeToSheetRow(record: ViewQueryRecordNode, uiColumns: UiColumn[]): SheetRow {
  const columns: SheetCellGroup[] = uiColumns.map((uiColumn): SheetCellGroup => {
    // eslint-disable-next-line sonarjs/todo-tag
    const sheetCellNodes = getRecordNodesWithInnerRowIndex(record, uiColumn.nodePath, false) // TODO
    return {
      uiColumn,
      innerCells: convertRecordNodeToInnerCells(record, uiColumn),
      height: sheetCellNodes.length === 0 ? 1 : Math.max(...sheetCellNodes.map((x) => x.innerRowIndexEnd)),
    }
  })

  const heights = columns.map((x) => x.height)
  return {
    height: heights.length === 0 ? 1 : Math.max(...heights),
    columns,
  }
}

function getRecordNodesWithInnerRowIndex(
  rootNode: ViewQueryRecordNode,
  nodePath: NodePath,
  shouldUseParentHeight: boolean, // relationがparentのとき
): SheetCellNode[] {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: なぜかrowがないことがありうるようなので、検査する
  // →真偽不明だがコメントに記述があるので残しておく
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (rootNode === undefined || rootNode === null) {
    return []
  }

  // rootのとき
  if (nodePath.length === 1) {
    return [
      {
        innerRowIndexStart: 0,
        innerRowIndexEnd: getInnerRowSpan(rootNode),
        recordNode: rootNode,
        parentRecordNode: undefined,
        height: getInnerRowSpan(rootNode),
      },
    ]
  }

  const parentRecordNodes = getParentRecordNodes(rootNode, nodePath)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (parentRecordNodes === undefined) {
    // 親が一切いない子など
    return []
  }

  const recordNodesWithIndex = parentRecordNodes.flatMap((parentRecordNode): SheetCellNode[] => {
    // 子がいない場合、空セルを親の高さで作る
    if (parentRecordNode.children.length === 0) {
      return [
        {
          innerRowIndexStart: parentRecordNode.parent.meta.innerRowIndexStart,
          innerRowIndexEnd: parentRecordNode.parent.meta.innerRowIndexEnd,
          recordNode: undefined,
          parentRecordNode: parentRecordNode.parent,
          height: getInnerRowSpan(parentRecordNode.parent),
        },
      ]
    }

    // parentな子が1つだけある場合、親の高さで作る
    if (shouldUseParentHeight && parentRecordNode.children.length === 1) {
      return [
        {
          innerRowIndexStart: parentRecordNode.parent.meta.innerRowIndexStart,
          innerRowIndexEnd: parentRecordNode.parent.meta.innerRowIndexEnd,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          recordNode: parentRecordNode.children[0]!,
          parentRecordNode: parentRecordNode.parent,
          height: getInnerRowSpan(parentRecordNode.parent),
        },
      ]
    }

    return parentRecordNode.children.map((recordNode) => ({
      innerRowIndexStart: recordNode.meta.innerRowIndexStart,
      innerRowIndexEnd: recordNode.meta.innerRowIndexEnd,
      recordNode,
      parentRecordNode: parentRecordNode.parent,
      height: getInnerRowSpan(recordNode),
    }))
  })

  // 親がいないセルに対して、blankCellを描画するために穴埋めする
  return addBlankCell(recordNodesWithIndex, rootNode.meta.innerRowIndexEnd)
}

function addBlankCell(nodesWithIndex: SheetCellNode[], rowInnerRowIndexEnd: number): SheetCellNode[] {
  const result: SheetCellNode[] = []
  for (const nodeWithIndex of nodesWithIndex) {
    const lastIndex = result.at(-1)?.innerRowIndexEnd ?? 0
    if (nodeWithIndex.innerRowIndexStart === lastIndex) {
      result.push(nodeWithIndex)
    } else {
      const emptyCell: SheetCellNode = {
        innerRowIndexStart: lastIndex,
        innerRowIndexEnd: nodeWithIndex.innerRowIndexStart,
        recordNode: undefined,
        parentRecordNode: undefined,
        height: nodeWithIndex.innerRowIndexStart - lastIndex,
      }
      result.push(emptyCell, nodeWithIndex)
    }
  }
  const lastIndex = result.at(-1)?.innerRowIndexEnd
  if (lastIndex !== undefined && lastIndex !== rowInnerRowIndexEnd) {
    result.push({
      innerRowIndexStart: lastIndex,
      innerRowIndexEnd: rowInnerRowIndexEnd,
      recordNode: undefined,
      parentRecordNode: undefined,
      height: rowInnerRowIndexEnd - lastIndex,
    })
  }

  return result
}

function getParentRecordNodes(rootNode: ViewQueryRecordNode, nodePath: string[]) {
  const parentRecordNodes = getRecordNodesByPath(rootNode, nodePath.slice(0, -1))
  return parentRecordNodes.map((parent) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const children = parent.children.find((tableNode) => tableNode.nodeName === nodePath.at(-1)!)?.children ?? []
    return {
      parent,
      children: children.map((child) => ({
        ...child,
        parent,
      })),
    }
  })
}
